import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import HeroCarousel from "@/components/HeroCarousel";
import ProductCard from "@/components/ProductCard";
import CartSheet from "@/components/CartSheet";
import HalfPizzaDialog from "@/components/HalfPizzaDialog";
import NoticePopup from "@/components/NoticePopup";
import CustomerPushPrompt from "@/components/CustomerPushPrompt";
import { api } from "@/lib/api";
import { MapPin, Phone, Clock, Pizza, Search, X } from "lucide-react";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [halfOpen, setHalfOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [store, setStore] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setStore(data)).catch(() => {});
  }, []);

  const DAY_NAMES = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const hoursText = store?.hours?.enabled
    ? `${store.hours.days.length === 7 ? "Todos os dias" : store.hours.days.map((d) => DAY_NAMES[d]).join(", ")}, ${store.hours.open} às ${store.hours.close}`
    : "Aberto todos os dias";

  const pizzas = useMemo(
    () => products.filter((p) => p.category === "pizza" && p.available !== false),
    [products]
  );

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/products")])
      .then(([c, p]) => {
        setCategories(c.data);
        setProducts(p.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => p.available !== false)
      .filter((p) => (q ? true : active === "todos" ? true : p.category === active))
      .filter((p) => (q ? p.name.toLowerCase().includes(q) : true));
  }, [products, active, query]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      (map[p.category] = map[p.category] || []).push(p);
    });
    return map;
  }, [filtered]);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <HeroCarousel />
      <CustomerPushPrompt />

      {store && !store.is_open && (
        <div data-testid="store-closed-banner" className="bg-red-600 text-white">
          <div className="max-w-6xl mx-auto px-5 py-3 flex items-center gap-2 text-sm font-semibold">
            <Clock className="w-4 h-4 shrink-0" />
            <span>Estamos FECHADOS no momento. Horário de funcionamento: {hoursText}.</span>
          </div>
        </div>
      )}

      <main id="cardapio" className="max-w-6xl mx-auto px-5 py-10">
        <div className="mb-6">
          <p className="uppercase tracking-[0.25em] text-primary text-xs font-bold">Cardápio</p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
            Escolha por categoria
          </h2>
        </div>

        <div className="sticky top-16 z-30 -mx-5 px-5 py-3 bg-background/80 backdrop-blur-md border-b border-border space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="menu-search-input"
              placeholder="Buscar no cardápio (ex.: calabresa, batata...)"
              className="w-full rounded-full border border-border bg-card pl-10 pr-10 py-2.5 text-sm outline-none focus:border-primary transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="menu-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Limpar busca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <Pill active={active === "todos"} onClick={() => setActive("todos")} testid="cat-todos">
              Todos
            </Pill>
            {categories.map((c) => (
              <Pill
                key={c.slug}
                active={active === c.slug}
                onClick={() => setActive(c.slug)}
                testid={`cat-${c.slug}`}
              >
                {c.name}
              </Pill>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-muted-foreground">Carregando cardápio...</p>
        ) : (
          <div className="mt-8 space-y-12">
            {!query && pizzas.length >= 2 && (active === "todos" || active === "pizza") && (
              <button
                onClick={() => setHalfOpen(true)}
                data-testid="open-half-pizza-btn"
                className="w-full text-left rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors p-5 flex items-center gap-4"
              >
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground shrink-0">
                  <Pizza className="w-6 h-6" />
                </span>
                <span>
                  <span className="block font-display font-black text-lg tracking-tight">
                    Monte sua pizza meio a meio
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    Escolha 2 sabores diferentes na mesma pizza.
                  </span>
                </span>
                <span className="ml-auto hidden sm:inline rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">
                  Montar
                </span>
              </button>
            )}
            {Object.keys(grouped).map((slug) => (
              <section key={slug} data-testid={`section-${slug}`}>
                <h3 className="font-display font-bold text-2xl tracking-tight mb-5">
                  {catName(slug)}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {grouped[slug].map((p, i) => (
                    <div key={p.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                      <ProductCard product={p} />
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {filtered.length === 0 && (
              <div className="py-16 text-center" data-testid="no-results">
                <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                <p className="text-muted-foreground">
                  Nenhum produto encontrado{query ? ` para "${query}"` : ""}.
                </p>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-border bg-card mt-8">
        <div className="max-w-6xl mx-auto px-5 py-10 grid gap-6 md:grid-cols-3">
          <div>
            <p className="font-display font-black text-xl">Paulista's Lanches &amp; Pizzas</p>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Pizzas, lanches, churrasco e muito mais, feitos na hora com carinho.
            </p>
          </div>
          <div className="text-sm space-y-2 text-muted-foreground">
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Rua José Pequeno 50, Alto Alegre</p>
            <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> <span data-testid="footer-hours">{hoursText}</span></p>
            <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> Peça pelo site e acompanhe seu pedido</p>
          </div>
          <div className="text-sm">
            <a href="/gestao/login" className="text-muted-foreground hover:text-primary transition-colors" data-testid="staff-login-link">
              Acesso da equipe
            </a>
          </div>
        </div>
      </footer>

      <CartSheet />
      <NoticePopup notice={store?.notice} />
      <HalfPizzaDialog pizzas={pizzas} open={halfOpen} onOpenChange={setHalfOpen} />
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card border-border hover:border-primary/50"
      }`}
    >
      {children}
    </button>
  );
}
